"use client";

import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

type SectionCardProps = {
  number: string;
  title: string;
  content: string;
  isExpanded?: boolean;
  onToggle?: () => void;
};

export function SectionCard({
  number,
  title,
  content,
  isExpanded = true,
  onToggle,
}: SectionCardProps) {
  return (
    <div className="flex flex-col w-full">
      {/* Desktop version - always expanded, no toggle */}
      <div className="hidden md:flex flex-col gap-2 items-start w-full">
        <div className="flex gap-2.5 items-center w-full">
          <div className="bg-d-secondary flex items-center justify-center w-6 h-6 rounded-full shrink-0">
            <span className="font-black text-d-black text-sm tracking-tight">
              {number}
            </span>
          </div>
          <p className="flex-1 font-semibold leading-6 text-d-black text-lg">
            {title}
          </p>
        </div>
        <div className="flex items-center justify-center pl-9 w-full">
          <p className="flex-1 font-medium leading-6 text-d-gray text-base">
            {content}
          </p>
        </div>
      </div>

      {/* Mobile version - collapsible */}
      <div className="md:hidden flex flex-col w-full">
        <button
          onClick={onToggle}
          className="flex gap-2.5 items-center w-full pb-3"
        >
          <div className="bg-d-secondary flex items-center justify-center w-5 h-5 rounded-full shrink-0">
            <span className="font-black text-d-black text-xs tracking-tight">
              {number}
            </span>
          </div>
          <p className="flex-1 font-medium text-left leading-6 text-d-black text-base">
            {title}
          </p>
          <motion.div
            animate={{
              rotate: isExpanded ? 180 : 0,
            }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <ChevronDown className="size-5 text-d-black" />
          </motion.div>
        </button>
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="pl-8 pr-3">
                <p className="font-medium leading-5 text-d-gray text-sm">
                  {content}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
